// "What am I missing to cook this?" — the Missing count on a recipe card.
//
// A saved recipe is matched against the current list with the same language-agnostic
// matcher the import sheet uses (`matchExisting` in recipe.ts), so "Tomater" in a
// recipe and "tomat" on the list are the same thing here too. No framework, no
// Supabase — unit-tested in recipeMatch.test.ts.

import type { Item, Recipe } from './supabase'
import { matchExisting, scaleAmount } from './recipe'

export interface IngredientLine {
  name: string
  /** Scaled to the portions being asked about, not as stored. */
  amount: number | null
  unit: string | null
  /** The list item covering this ingredient, or null when nothing does. */
  item: Item | null
  /** That item is already ticked off — i.e. bought, not merely planned. */
  bought: boolean
  /** Unticked at import: part of the dish, but not something to shop for. */
  pantry: boolean
}

export interface RecipeStatus {
  /** Every ingredient of the dish, pantry ones included. */
  lines: IngredientLine[]
  /** The pantry half of `lines`, shown apart and counted in nothing. */
  pantryLines: IngredientLine[]
  missing: IngredientLine[]
  /** How many ingredients are covered by the list, of `total`. */
  covered: number
  total: number
}

/**
 * How much to scale stored amounts by for `servings` portions. Falls back to 1
 * whenever either side is unknown — a recipe with no servings is shown as
 * written rather than silently mis-scaled.
 */
export function scaleFactorFor(recipe: Pick<Recipe, 'servings'>, servings: number | null): number {
  if (!recipe.servings || !servings) return 1
  return servings / recipe.servings
}

/**
 * Match one recipe against the list. An ingredient counts as covered whether the
 * matching item is still to buy or already ticked off; the distinction is kept
 * on the line (`bought`) so the UI can say which.
 *
 * Pantry ingredients are matched like any other — a line still says whether salt
 * happens to be on the list — but they are kept out of `missing`, `covered` and
 * `total`: those three answer "what do I still have to shop for?", and the
 * household's staples are not part of that question.
 */
export function recipeStatus(
  recipe: Pick<Recipe, 'servings' | 'ingredients'>,
  items: Item[],
  servings: number | null = null,
): RecipeStatus {
  const factor = scaleFactorFor(recipe, servings)

  const lines: IngredientLine[] = recipe.ingredients.map(ing => {
    const match = matchExisting(ing.name, items, { includeDone: true })
    return {
      name: ing.name,
      amount: scaleAmount(ing.amount, factor),
      unit: ing.unit,
      item: match,
      bought: match?.done ?? false,
      pantry: ing.pantry === true,
    }
  })

  const pantryLines = lines.filter(l => l.pantry)
  const shopping = lines.filter(l => !l.pantry)
  const missing = shopping.filter(l => l.item === null)
  return {
    lines,
    pantryLines,
    missing,
    covered: shopping.length - missing.length,
    total: shopping.length,
  }
}
